import { useMemo, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  ContractStatusEnum,
  type Contract,
  type Installment,
} from "@/generated/types";
import { useMarkInstallmentAsPaid } from "@/hooks/useMarkInstallmentAsPaid";
import { useUnmarkInstallmentAsPaid } from "@/hooks/useUnmarkInstallmentAsPaid";
import { useDeleteContract } from "@/hooks/useDeleteContract";
import type { MutationCallbacks } from "@/hooks/useMarkInstallmentAsPaid";
import Currency from "../Currency";
import DateDisplay from "../DateDisplay";
import InstallmentRow from "./InstallmentRow";
import ContractMemberList from "./ContractMemberList";
import DeleteContractDialog from "./DeleteContractDialog";

interface ContractCardProps {
  contract: Contract;
  projectId: string;
  showMembers?: boolean;
  showAdminActions?: boolean;
}

const STATUS_VARIANT: Record<
  ContractStatusEnum,
  "default" | "success" | "destructive"
> = {
  [ContractStatusEnum.Pending]: "default",
  [ContractStatusEnum.Paid]: "success",
  [ContractStatusEnum.Overdue]: "destructive",
  [ContractStatusEnum.Complete]: "success",
};

const STATUS_LABEL: Record<ContractStatusEnum, string> = {
  [ContractStatusEnum.Pending]: "Pendente",
  [ContractStatusEnum.Paid]: "Pago",
  [ContractStatusEnum.Overdue]: "Atrasado",
  [ContractStatusEnum.Complete]: "Concluído",
};

export default function ContractCard({
  contract,
  projectId,
  showMembers = false,
  showAdminActions = false,
}: ContractCardProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const { markAsPaid, loading: marking } = useMarkInstallmentAsPaid(projectId, {
    onError: () => setActionError("Não foi possível marcar como pago."),
  });
  const { unmarkAsPaid, loading: unmarking } = useUnmarkInstallmentAsPaid(projectId, {
    onError: () => setActionError("Não foi possível desfazer o pagamento."),
  });
  const { deleteContract, loading: deleting } = useDeleteContract(projectId, {
    onError: () => setActionError("Não foi possível excluir o contrato."),
  });

  const actionLoading = marking || unmarking || deleting;

  const sortedInstallments = useMemo(
    () => [...contract.installments].sort((a, b) => a.number - b.number),
    [contract.installments]
  );

  const paidCount = contract.paidInstallmentsCount;
  const hasPaidInstallments = paidCount > 0;
  const installmentCount = contract.installmentsCount;

  const handleMarkAsPaid = (
    installment: Installment,
    paidAtDate: string,
    callbacks?: MutationCallbacks
  ) => {
    setActionError(null);
    markAsPaid(installment, paidAtDate, callbacks);
  };

  const handleUnmarkAsPaid = (installment: Installment) => {
    setActionError(null);
    unmarkAsPaid(installment);
  };

  const handleDeleteContract = () => {
    setActionError(null);
    deleteContract(contract.id);
  };

  return (
    <Collapsible asChild>
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full text-left cursor-pointer min-h-11">
            <CardContent className="p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="text-base font-light text-primary-950 dark:text-white truncate">
                      {contract.name}
                    </h4>
                    <Badge variant={STATUS_VARIANT[contract.status]}>
                      {STATUS_LABEL[contract.status]}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-primary-600 dark:text-primary-400">
                    <span>
                      {installmentCount} parcela
                      {installmentCount !== 1 ? "s" : ""}
                    </span>
                    <span>
                      {paidCount} de {installmentCount} pagas
                    </span>
                    <span>
                      Pago: <Currency value={contract.paidTotal} />
                    </span>
                    <span>
                      Restante: <Currency value={contract.remainingBalance} />
                    </span>
                    <DateDisplay date={contract.startDate} />
                    <Currency value={contract.totalValue} />
                  </div>
                  {showMembers && (
                    <ContractMemberList users={contract.users} />
                  )}
                </div>
                {showAdminActions && (
                  <DeleteContractDialog
                    contractName={contract.name}
                    onConfirm={handleDeleteContract}
                    disabled={actionLoading || hasPaidInstallments}
                    disabledReason={
                      hasPaidInstallments
                        ? "Não é possível excluir contrato com parcelas pagas."
                        : undefined
                    }
                  />
                )}
              </div>
            </CardContent>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-primary-100 dark:border-primary-800">
            <CardContent className="p-6 pt-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Parcela</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pago em</TableHead>
                    {showAdminActions && <TableHead>Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedInstallments.map((installment) => (
                    <InstallmentRow
                      key={installment.id}
                      installment={installment}
                      showAdminActions={showAdminActions}
                      onMarkAsPaid={showAdminActions ? handleMarkAsPaid : undefined}
                      onUnmarkAsPaid={showAdminActions ? handleUnmarkAsPaid : undefined}
                      actionLoading={actionLoading}
                      actionError={actionError}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
